import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import liff from '@line/liff';
const API_BASE = import.meta.env.VITE_API_BASE || '';
export function ChecklistPage({ profile }) {
    const [params] = useSearchParams();
    const eventId = params.get('eventId') || '';
    const studentId = params.get('studentId') || '';
    const guardianId = params.get('guardianId') || '';
    const token = params.get('token') || '';
    const [step, setStep] = useState('loading');
    const [items, setItems] = useState([]);
    const [results, setResults] = useState(new Map());
    const [errorMsg, setErrorMsg] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    useEffect(() => {
        loadItems();
    }, [eventId]);
    async function loadItems() {
        if (!eventId) {
            setStep('error');
            setErrorMsg('ข้อมูลไม่ครบ');
            return;
        }
        try {
            // Fetch event items (public, no auth needed)
            const res = await fetch(`${API_BASE}/api/orientation/events/${eventId}`);
            const data = await res.json();
            if (data.success && data.data.items) {
                setItems(data.data.items);
                // Default all to checked (received)
                const defaultResults = new Map();
                data.data.items.forEach((item) => defaultResults.set(item.id, true));
                setResults(defaultResults);
                setStep('checklist');
            }
            else {
                setStep('error');
                setErrorMsg('ไม่พบข้อมูลกิจกรรม');
            }
        }
        catch {
            setStep('error');
            setErrorMsg('เกิดข้อผิดพลาด');
        }
    }
    function toggleItem(itemId) {
        setResults((prev) => {
            const next = new Map(prev);
            next.set(itemId, !next.get(itemId));
            return next;
        });
    }
    async function handleSubmit() {
        setIsSubmitting(true);
        try {
            const itemResults = items.map((item) => ({
                itemId: item.id,
                received: results.get(item.id) ?? false,
            }));
            const res = await fetch(`${API_BASE}/api/orientation/submissions`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    eventId,
                    studentId,
                    guardianId,
                    token,
                    itemResults,
                    consentGivenAt: new Date().toISOString(),
                }),
            });
            const data = await res.json();
            if (data.success) {
                setStep('success');
            }
            else {
                setErrorMsg(data.error?.messageTh || 'เกิดข้อผิดพลาด');
            }
        }
        catch {
            setErrorMsg('เกิดข้อผิดพลาด');
        }
        setIsSubmitting(false);
    }
    const checkedCount = [...results.values()].filter(Boolean).length;
    const totalItems = items.length;
    // Group items by category
    const grouped = items.reduce((acc, item) => {
        if (!acc[item.category])
            acc[item.category] = [];
        acc[item.category].push(item);
        return acc;
    }, {});
    const categoryIcons = {
        'เอกสาร': '📚',
        'อุปกรณ์': '🎒',
        'เครื่องแบบ': '👔',
    };
    if (step === 'loading') {
        return (_jsx("div", { className: "flex min-h-screen items-center justify-center bg-gray-50", children: _jsx("div", { className: "text-gray-400", children: "\u0E01\u0E33\u0E25\u0E31\u0E07\u0E42\u0E2B\u0E25\u0E14..." }) }));
    }
    if (step === 'error') {
        return (_jsx("div", { className: "flex min-h-screen items-center justify-center bg-gray-50 px-4", children: _jsxs("div", { className: "text-center", children: [_jsx("div", { className: "text-4xl", children: "\u274C" }), _jsx("p", { className: "mt-4 text-sm text-gray-600", children: errorMsg })] }) }));
    }
    if (step === 'success') {
        const missing = items.filter((i) => !results.get(i.id));
        return (_jsx("div", { className: "flex min-h-screen items-center justify-center bg-gray-50 px-4", children: _jsxs("div", { className: "mx-auto max-w-sm text-center", children: [_jsx("div", { className: "text-5xl", children: "\u2705" }), _jsx("h1", { className: "mt-4 text-lg font-bold text-gray-900", children: "\u0E2A\u0E48\u0E07\u0E02\u0E49\u0E2D\u0E21\u0E39\u0E25\u0E2A\u0E33\u0E40\u0E23\u0E47\u0E08" }), _jsxs("p", { className: "mt-2 text-sm text-gray-600", children: ["\u0E44\u0E14\u0E49\u0E23\u0E31\u0E1A ", checkedCount, "/", totalItems, " \u0E23\u0E32\u0E22\u0E01\u0E32\u0E23"] }), missing.length > 0 && (_jsxs("div", { className: "mt-4 rounded-lg bg-amber-50 p-3 text-left text-sm text-amber-700", children: [_jsx("p", { className: "font-medium", children: "\u0E22\u0E31\u0E07\u0E44\u0E21\u0E48\u0E44\u0E14\u0E49\u0E23\u0E31\u0E1A:" }), missing.map((i) => (_jsxs("p", { children: ["\u2022 ", i.name] }, i.id)))] })), _jsx("button", { onClick: () => liff.isInClient() && liff.closeWindow(), className: "mt-6 w-full rounded-lg border border-gray-200 py-3 text-sm text-gray-600", children: "\u0E1B\u0E34\u0E14\u0E2B\u0E19\u0E49\u0E32\u0E15\u0E48\u0E32\u0E07\u0E19\u0E35\u0E49" })] }) }));
    }
    // Checklist step
    return (_jsx("div", { className: "min-h-screen bg-gray-50 px-4 py-6", children: _jsxs("div", { className: "mx-auto max-w-sm", children: [_jsx("p", { className: "text-xs text-gray-400", children: "\u0E02\u0E31\u0E49\u0E19\u0E15\u0E2D\u0E19 3/4" }), _jsx("h1", { className: "mt-1 text-lg font-bold text-gray-900", children: "\u0E40\u0E1B\u0E34\u0E14\u0E16\u0E38\u0E07 \u0E41\u0E25\u0E49\u0E27\u0E40\u0E0A\u0E47\u0E04\u0E02\u0E2D\u0E07" }), _jsxs("p", { className: "mt-2 text-sm text-gray-500", children: [checkedCount, "/", totalItems, " \u0E23\u0E32\u0E22\u0E01\u0E32\u0E23"] }), _jsx("div", { className: "mt-1 h-1.5 overflow-hidden rounded-full bg-gray-200", children: _jsx("div", { className: "h-full rounded-full bg-[#06C755] transition-all", style: { width: `${totalItems > 0 ? (checkedCount / totalItems) * 100 : 0}%` } }) }), _jsx("div", { className: "mt-4 space-y-4", children: Object.entries(grouped).map(([category, categoryItems]) => (_jsxs("div", { children: [_jsxs("h2", { className: "mb-2 flex items-center gap-1 text-sm font-semibold text-gray-700", children: [categoryIcons[category] || '📦', " ", category] }), _jsx("div", { className: "space-y-1", children: categoryItems.map((item) => {
                                    const checked = results.get(item.id) ?? false;
                                    return (_jsxs("button", { onClick: () => toggleItem(item.id), className: `flex w-full items-center gap-3 rounded-lg px-3 py-3 text-left text-sm transition-colors ${checked ? 'bg-white shadow-sm' : 'bg-red-50'}`, children: [_jsx("span", { className: `flex h-5 w-5 items-center justify-center rounded text-xs ${checked ? 'bg-[#06C755] text-white' : 'border-2 border-red-300'}`, children: checked && '✓' }), _jsx("span", { className: checked ? 'text-gray-900' : 'text-red-700', children: item.name }), !checked && (_jsx("span", { className: "ml-auto text-xs text-red-500", children: "\u0E22\u0E31\u0E07\u0E44\u0E21\u0E48\u0E44\u0E14\u0E49\u0E23\u0E31\u0E1A !" }))] }, item.id));
                                }) })] }, category))) }), _jsx("button", { onClick: handleSubmit, disabled: isSubmitting, className: "mt-6 w-full rounded-lg bg-[#06C755] py-3 text-sm font-semibold text-white disabled:opacity-50", children: isSubmitting ? 'กำลังส่ง...' : 'ถัดไป' })] }) }));
}
//# sourceMappingURL=ChecklistPage.js.map